import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarDays, CheckSquare2, Mail, MessageCircle } from "lucide-react";
import type { JSX, ReactNode } from "react";
import { Link } from "react-router-dom";

import { apiRequest } from "../../common/api/apiClient.js";
import type { AppDescriptorDTO, ChannelDTO, EventDTO, MailAccountDTO, MailMessagePageDTO, MeDTO, TaskProjectSummaryDTO } from "../../common/api/types.js";
import { AppIcon } from "../../common/components/AppIcon.js";

const roleLabels: Record<string, string> = {
  OWNER: "Eigentümer",
  ADMIN: "Administrator",
  MEMBER: "Mitglied",
  GUEST: "Gast",
};

export function HomePage({ me, apps }: { me: MeDTO; apps: AppDescriptorDTO[] }): JSX.Element {
  const openable = apps.filter((app) => app.id !== "home");
  const enabled = new Set(apps.map((app) => app.id));
  const mailAccounts = useQuery({
    queryKey: ["home", "mail", "accounts"],
    queryFn: () => apiRequest<MailAccountDTO[]>("/mail/accounts"),
    enabled: enabled.has("mail"),
    retry: false,
  });
  const personalMailbox = mailAccounts.data?.find((account) => account.personal) ?? null;
  const mail = useQuery({
    queryKey: ["home", "mail", "inbox", personalMailbox?.id],
    queryFn: () => apiRequest<MailMessagePageDTO>(`/mail/accounts/${encodeURIComponent(personalMailbox?.id ?? "")}/messages?view=inbox&limit=5`),
    enabled: Boolean(personalMailbox),
  });
  const projects = useQuery({
    queryKey: ["home", "tasks"],
    queryFn: () => apiRequest<TaskProjectSummaryDTO[]>("/tasks/projects"),
    enabled: enabled.has("tasks"),
  });
  const channels = useQuery({
    queryKey: ["home", "chat"],
    queryFn: () => apiRequest<ChannelDTO[]>("/chat/channels"),
    enabled: enabled.has("chat"),
  });
  const dayKey = new Date().toISOString().slice(0, 10);
  const events = useQuery({
    queryKey: ["home", "calendar", dayKey],
    queryFn: () => {
      const from = new Date();
      const to = new Date(from.getTime() + 14 * 86_400_000);
      return apiRequest<EventDTO[]>(`/calendar/events?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`);
    },
    enabled: enabled.has("calendar"),
  });
  const inbox = mail.data?.messages ?? [];

  return (
    <div className="w-full space-y-7 p-4 sm:p-6 lg:p-8">
      <section>
        <p className="text-sm font-semibold text-brand">{greeting()}</p>
        <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Willkommen, {me.user.displayName}</h2>
        <p className="mt-1 text-sm text-ink-muted">
          {me.activeTenant ? `${me.activeTenant.name} · ${roleLabels[me.role ?? ""] ?? me.role ?? ""}` : "Kein Unternehmen ausgewählt"}
        </p>
      </section>

      <section aria-label="Deine Übersicht" className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {enabled.has("mail") ? <DashboardCard title="E-Mails" href="/mail" icon={<Mail className="h-5 w-5" />} color="bg-blue-500/10 text-blue-600" count={personalMailbox?.folders.find((folder) => folder.role === "inbox")?.unread ?? 0} empty="Keine neuen E-Mails">
          {inbox.map((message) => <DashboardRow key={message.id} href={`/mail/${message.id}?account=${encodeURIComponent(personalMailbox?.id ?? "")}`} title={message.subject || "(Kein Betreff)"} meta={`${message.from?.name || message.from?.email || "Unbekannt"} · ${relativeTime(message.receivedAt)}`} emphasized={!message.seen} />)}
        </DashboardCard> : null}

        {enabled.has("tasks") ? <DashboardCard title="Aufgaben" href="/tasks" icon={<CheckSquare2 className="h-5 w-5" />} color="bg-violet-500/10 text-violet-600" count={(projects.data ?? []).reduce((sum, project) => sum + project.issueCount, 0)} empty="Keine Projekte vorhanden" loading={projects.isPending}>
          {(projects.data ?? []).map((project) => <DashboardRow key={project.id} href={`/tasks/${project.id}`} title={project.name} meta={`${project.key} · ${project.issueCount} Issues`} />)}
        </DashboardCard> : null}

        {enabled.has("calendar") ? <DashboardCard title="Nächste Termine" href="/calendar" icon={<CalendarDays className="h-5 w-5" />} color="bg-emerald-500/10 text-emerald-600" count={events.data?.length} empty="Keine Termine in den nächsten 14 Tagen" loading={events.isPending}>
          {(events.data ?? []).map((event) => <DashboardRow key={event.id} href={`/calendar?week=${event.startsAt.slice(0, 10)}&event=${event.id}`} title={event.title} meta={formatEvent(event)} accent={event.calendarColor} />)}
        </DashboardCard> : null}

        {enabled.has("chat") ? <DashboardCard title="Chats" href="/chat" icon={<MessageCircle className="h-5 w-5" />} color="bg-amber-500/10 text-amber-600" count={(channels.data ?? []).reduce((sum, channel) => sum + channel.unreadCount, 0)} empty="Noch keine Unterhaltungen" loading={channels.isPending}>
          {(channels.data ?? []).map((channel) => <DashboardRow key={channel.id} href={`/chat/${channel.id}`} title={channel.title} meta={channel.lastMessagePreview ?? channel.topic ?? "Unterhaltung öffnen"} emphasized={channel.unreadCount > 0} badge={channel.unreadCount || undefined} />)}
        </DashboardCard> : null}
      </section>

      <section>
        <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-ink-muted">Alle Apps</h3>
        {openable.length === 0 ? <p className="rounded-xl border border-dashed border-line p-6 text-sm text-ink-muted">Für dieses Unternehmen sind noch keine Apps freigeschaltet.</p> : <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">{openable.map((app) => <li key={app.id}><Link to={app.route} className="flex min-h-24 flex-col items-start justify-between gap-3 rounded-2xl border border-line bg-surface-raised p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-brand hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"><AppIcon name={app.icon} className="h-6 w-6 text-brand" /><span className="text-sm font-bold text-ink">{app.name}</span></Link></li>)}</ul>}
      </section>
    </div>
  );
}

function DashboardCard({ title, href, icon, color, count, empty, loading = false, children }: { title: string; href: string; icon: ReactNode; color: string; count?: number; empty: string; loading?: boolean; children: ReactNode }): JSX.Element {
  const hasChildren = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return <article className="min-w-0 overflow-hidden rounded-2xl border border-line bg-surface-raised shadow-sm"><header className="flex min-h-16 items-center gap-3 border-b border-line px-4"><span className={`grid h-10 w-10 place-items-center rounded-xl ${color}`}>{icon}</span><h3 className="min-w-0 flex-1 truncate font-extrabold text-ink">{title}</h3>{count ? <span className="rounded-full bg-brand px-2 py-0.5 text-xs font-bold text-brand-ink">{count}</span> : null}<Link to={href} aria-label={`${title} öffnen`} className="grid h-9 w-9 place-items-center rounded-lg text-ink-muted hover:bg-surface-sunken hover:text-ink"><ArrowRight className="h-4 w-4" /></Link></header><div className="max-h-[13.5rem] min-h-[4.5rem] divide-y divide-line overflow-y-auto">{loading ? <p className="p-5 text-sm text-ink-muted">Wird geladen …</p> : hasChildren ? children : <p className="p-5 text-sm text-ink-muted">{empty}</p>}</div></article>;
}

function DashboardRow({ href, title, meta, emphasized = false, badge, accent }: { href: string; title: string; meta: string; emphasized?: boolean; badge?: number; accent?: string }): JSX.Element {
  return <Link to={href} className="flex min-h-[4.5rem] items-center gap-3 px-4 py-3 hover:bg-surface-sunken"><span aria-hidden="true" className="h-9 w-1 shrink-0 rounded-full" style={{ backgroundColor: accent ?? "transparent" }} /><span className="min-w-0 flex-1"><span className={`block truncate text-sm text-ink ${emphasized ? "font-extrabold" : "font-semibold"}`}>{title}</span><span className="mt-0.5 block truncate text-xs text-ink-muted">{meta}</span></span>{badge ? <span className="grid h-6 min-w-6 place-items-center rounded-full bg-brand px-1.5 text-xs font-bold text-brand-ink">{badge}</span> : null}</Link>;
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 11) return "Guten Morgen";
  if (hour < 18) return "Guten Tag";
  return "Guten Abend";
}

function relativeTime(value: string): string {
  const minutes = Math.round((new Date(value).getTime() - Date.now()) / 60_000);
  const useMinutes = Math.abs(minutes) < 60;
  return new Intl.RelativeTimeFormat("de", { numeric: "auto" }).format(useMinutes ? minutes : Math.round(minutes / 60), useMinutes ? "minute" : "hour");
}

function formatEvent(event: EventDTO): string {
  const startsAt = new Date(event.startsAt);
  return `${startsAt.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })} · ${event.allDay ? "ganztägig" : startsAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}${event.location ? ` · ${event.location}` : ""}`;
}
